export type ConversionType = "initiation" | "lead" | "prospect" | "sale" | "custom";

export const PALDOCK_CONVERSION_BOOTSTRAP =
    '(function(w,p){p=w.paldock=w.paldock||{};p.q=p.q||[];p.conversion=p.conversion||function(){p.q.push(["conversion"].concat([].slice.call(arguments)))}})(window);';

const getActionForType = (type: ConversionType): string | null => {
    switch (type) {
        case "initiation":
            return null;
        case "lead":
            return "update";
        default:
            return "create";
    }
};

/** Reads existing tag query param values from a previously generated initiation pixel's
 * `<script async src="...">` URL, so manual edits survive toggling tag pills instead of always
 * resetting back to "REPLACE_ME". */
const extractInitiationQueryValues = (pixelCode: string): Record<string, string> => {
    const match = /src="([^"]+)"/.exec(pixelCode);
    if (!match) return {};
    try {
        const url = new URL(match[1]);
        const values: Record<string, string> = {};
        url.searchParams.forEach((value, key) => {
            values[key] = value;
        });
        return values;
    } catch {
        return {};
    }
};

/** Reads existing simple `key: 'value',` field values from a previously generated
 * paldock.conversion({...}) snippet, so manual edits survive toggling tag pills. Does not attempt
 * to preserve the nested queryParam/cookieParam blocks — those always regenerate to their
 * placeholder key/value, since diffing a renamed nested key unambiguously isn't feasible here. */
const extractConversionFieldValues = (pixelCode: string): Record<string, string> => {
    const values: Record<string, string> = {};
    const lineRegex = /^\s*(\w+):\s*'([^']*)',?\s*$/gm;
    let match: RegExpExecArray | null;
    while ((match = lineRegex.exec(pixelCode)) !== null) {
        values[match[1]] = match[2];
    }
    return values;
};

interface BuildPixelCodeParams {
    conversionType: ConversionType;
    tags: string[];
    tenantSlug: string;
    pixelCdnUrl?: string;
    pixelVersion?: string;
    currentPixelCode?: string;
}

export const buildPixelCode = ({
    conversionType,
    tags,
    tenantSlug,
    pixelCdnUrl,
    pixelVersion,
    currentPixelCode = "",
}: BuildPixelCodeParams): string => {
    if (conversionType === "initiation") {
        const baseUrl = `${pixelCdnUrl}/paldock-pixel-${pixelVersion}.js?t=${tenantSlug}&cookie=1`;
        const existingValues = extractInitiationQueryValues(currentPixelCode);
        const params = tags.map((tag) => `${tag}=${existingValues[tag] ?? "REPLACE_ME"}`);
        const fullUrl = params.length > 0 ? `${baseUrl}&${params.join("&")}` : baseUrl;

        return `<!-- pixel load -->
<script async src="${fullUrl}"></script>`;
    }

    const action = getActionForType(conversionType);
    const existingValues = extractConversionFieldValues(currentPixelCode);

    const lines: string[] = [];
    lines.push(`      action: '${action}',`);
    lines.push(`      type: '${conversionType}',`);
    lines.push(`      advertiser_id: '${existingValues.advertiser_id ?? "REPLACE_ME"}',`);

    tags.forEach((tag) => {
        if (tag === "queryParam") {
            lines.push(`      queryParam: {`);
            lines.push(`          REPLACE_KEY: 'REPLACE_ME',`);
            lines.push(`      },`);
        } else if (tag === "cookieParam") {
            lines.push(`      cookieParam: {`);
            lines.push(`          REPLACE_KEY: 'REPLACE_ME',`);
            lines.push(`      },`);
        } else {
            lines.push(`      ${tag}: '${existingValues[tag] ?? "REPLACE_ME"}',`);
        }
    });

    return `<script>
  ${PALDOCK_CONVERSION_BOOTSTRAP}

  paldock.conversion({
${lines.join("\n")}
  });
</script>`;
};
